<template>
  <div id="home">
    <div class="px-3 py-3 pt-md-5 pb-md-4 mx-auto text-center">
      <h1 class="display-4 mr-2">Browse</h1>
    </div>

    <form @submit.prevent="sendRequest(query)" class="querybar pt-2 pb-1 mb-3">
      <div class="input-group input-group-lg mt-3 mb-3">
        <div class="input-group-prepend">
          <span class="input-group-text" style="background-color: rgba(0,0,0,.03);"><span class="d-none d-md-inline">https://data.silversky.dev</span>/v1/</span>
        </div>
        <input type="text" class="form-control" style="font-weight: bold;" v-model="query">
        <span class="input-group-append">
          <button type="submit" class="btn btn-lg btn-block btn-outline-success">
            <play-icon size="1.0x" style="transform: translateY(-2px)" class="mr-1"></play-icon> Send <span class="d-none d-md-inline">Request</span>
          </button>
        </span>
      </div>
    </form>

    <div class="alert alert-warning" role="alert" v-show="warning">
      <h4 class="alert-heading">
        <alert-triangle-icon size="1.0x" style="transform: translateY(-3px)" class="mr-2"></alert-triangle-icon>{{warning}}
      </h4>
      <p class="mb-0">Have a look at the examples below for some inspiration.</p>
    </div>

    <div class="alert alert-danger" role="alert" v-show="error">
      <h4 class="alert-heading">
        <alert-circle-icon size="1.0x" style="transform: translateY(-3px)" class="mr-2"></alert-circle-icon>{{error}}
      </h4>
      <p>Sorry, the data API was not able to handle this request. Please check your spelling and try again.</p>
      <hr>
      <p class="mb-0">Have a look at the examples below for some inspiration.</p>
    </div>

    <div class="alert alert-secondary" v-show="grid.length > 0">
      Fetched {{grid.length}} record<span v-show="grid.length > 1">s</span> in {{ms}} ms
    </div>

    <div class="card-deck mb-3" v-show="grid.length > 0">
      <div class="card mb-4 shadow-sm">
        <div class="card-header">
          <h4 class="my-0 font-weight-normal">
            <grid-icon size="1.0x" style="transform: translateY(-3px)" class="mr-2"></grid-icon> Grid
          </h4>
        </div>
        <div class="card-body">
          <table class="table mb-0">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th class="d-none d-md-table-cell" scope="col" v-for="key in keys" v-bind:key="key" v-on:click="sort(key)" style="cursor: pointer;">
                  {{key}} <span v-if="currentSort == key"><span v-if="currentSortDir == 'desc'">↓</span><span v-else>↑</span></span>
                </th>
                <th class="d-table-cell d-md-none">data</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, id) in gridShow" v-bind:key="id">
                <th scope="row">{{id+1}}</th>
                <td class="d-none d-md-table-cell" v-for="key in keys" v-bind:key="key">
                  <span v-if="key == 'key'"><a class="key clickable" v-on:click="sendRequest('latest/10/'+row['key'])">{{row['key']}}</a></span>
                  <span v-else-if="row[key] == null" class="gray">null</span>
                  <span v-else-if="JSON.stringify(row[key]).startsWith('{')" style="cursor: help;" v-bind:title="JSON.stringify(row[key], null, 2)">{...}</span>
                  <span v-else-if="key == 'value'" v-bind:title="row[key]">{{format(row[key])}}</span>
                  <span v-else>{{row[key]}}</span>
                </td>
                <td class="d-table-cell d-md-none">
                  <div v-for="key in keys" v-bind:key="key">
                    {{key}}: {{row[key]}}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="alert alert-warning mt-3" role="alert" v-show="isLimited">
            <h4 class="alert-heading">Display Limit</h4>
            <p>Showing only the first {{limit}} rows.</p>
            <hr>
            <p class="mb-0">Show <a href="#" class="alert-link" v-on:click="limit = grid.length">all</a> rows.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div v-bind:class="{'col-lg-6': chart, 'col-lg-12': !chart}" v-show="json">
        <div class="card mb-4 shadow-sm">
          <div class="card-header">
            <h4 class="my-0 font-weight-normal">
              <code-icon size="1.0x" style="transform: translateY(-3px)" class="mr-2"></code-icon> JSON
            </h4>
          </div>
          <div class="card-body">
            <pre class="mb-0">{{jsonShow}}</pre>
            <div class="alert alert-warning mt-3" role="alert" v-show="isLimited">
              <h4 class="alert-heading">Display Limit</h4>
              <p>Showing only the first {{limit}} rows.</p>
              <hr>
              <p class="mb-0">Show <a href="#" class="alert-link" v-on:click="limit = grid.length">all</a> rows.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6" v-show="chart">
        <div class="card mb-4 shadow-sm">
          <div class="card-header">
            <h4 class="my-0 font-weight-normal">
              <trending-up-icon size="1.0x" style="transform: translateY(-3px)" class="mr-2"></trending-up-icon> Chart
            </h4>
          </div>
          <div class="card-body pb-3">
            <LineChart v-if="type=='line'" v-bind:data="chart" />
            <BarChart v-if="type=='bar'" v-bind:data="chart" />
          </div>
        </div>
      </div>
    </div>

    <div id="examples" class="pt-md-3 pb-4 mx-auto text-left">
      <h3 class="mr-2 font-weight-normal">Featured</h3>

      <div v-for="(example, id) in $parent.featured" v-bind:key="id">
        <a href="#" v-on:click="sendRequest(example)">http://data.silversky.dev/v1/{{example}}</a>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    GridIcon,
    CodeIcon,
    TrendingUpIcon,
    PlayIcon,
    AlertCircleIcon,
    AlertTriangleIcon
  } from 'vue-feather-icons'

  import LineChart from './LineChart.vue'
  import BarChart from './BarChart.vue'

  export default {
    name: 'Home',
    data: function() {
      return {
        query: '',
        keys: [],
        grid: [],
        currentSort: '',
        currentSortDir: 'asc',
        json: '',
        chart: false,
        limit: 10,
        error: '',
        warning: '',
        ms: 0
      };
    },
    computed: {
      gridShow: function() {
        if (this.currentSort) {
          return Array.from(this.grid).sort((a, b) => {
            let modifier = 1;
            if (this.currentSortDir === 'desc') {
              modifier = -1;
            }
            if (!isNaN(a[this.currentSort]) && !isNaN(b[this.currentSort])) {
              return (a[this.currentSort] - b[this.currentSort]) * modifier;
            }
            if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
            if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
            return 0;
          }).slice(0, this.limit);
        }

        return this.grid.slice(0, this.limit);
      },
      jsonShow: function() {
        return JSON.stringify(this.grid.slice(0, this.limit), null, 2);
      },
      isLimited: function() {
        return this.gridShow.length < this.grid.length;
      }
    },
    methods: {
      sendRequest: function(query) {
        this.query = query;
        this.error = '';
        this.warning = '';
        this.grid = [];
        this.json = '';
        this.chart = false;
        this.ms = 0;

        if (query.length == 0) {
          this.warning = 'Please enter a query string first';
          return;
        }

        var t0 = Date.now();

        fetch("https://data.silversky.dev/v1/" + query)
          .then(response => response.json()).then(json => {
            var t1 = Date.now();

            this.ms = t1 - t0;

            // Table
            this.grid = json;

            if (!Array.isArray(this.grid)) {
              this.grid = [this.grid];
            }
            this.keys = Object.keys(this.grid[0]);

            // JSON
            this.json = JSON.stringify(json, null, 2);

            // Chart
            const presets = {
              red: 'rgb(255, 99, 132)',
              orange: 'rgb(255, 159, 64)',
              yellow: 'rgb(255, 205, 86)',
              green: 'rgb(75, 192, 192)',
              blue: 'rgb(54, 162, 235)',
              purple: 'rgb(153, 102, 255)',
              grey: 'rgb(201, 203, 207)'
            };

            const colors = [presets.red, presets.blue, presets.green, presets.yellow, presets.purple, presets.orange, presets.grey];

            var keys = {};
            this.grid.forEach(row => {
              if (!keys[row.key]) {
                keys[row.key] = [];
              }
              if (!isNaN(row.value)) {
                keys[row.key].push({
                  x: row.timestamp,
                  y: parseFloat(row.value)
                });
              }
            })

            if (this.grid.length > 1 && Object.keys(keys).length <= 7) {
              this.chart = [];
              this.type = this.grid.length > Object.keys(keys).length ? 'line' : 'bar';
              var i = 0;
              Object.keys(keys).forEach(key => {
                this.chart.push({
                  label: key,
                  fill: false,
                  backgroundColor: colors[i],
                  borderColor: colors[i],
                  lineTension: 0,
                  data: keys[key]
                });
                i++;
              })
            }

          }).catch(err => {
            if (err) {
              this.error = "Could not process: '" + this.query + "'";
            }
          });
      },
      sort: function(key) {
        if (key === this.currentSort) {
          this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
        }
        this.currentSort = key;
      },
      format: function(value) {
        if (!isNaN(value)) {
          return Number(value).toLocaleString();
        }
        return value.slice(0, 40) + (value.length > 40?'...':'');
      }
    },
    created: function() {
      if (this.$parent.query) {
        this.sendRequest(this.$parent.query);
        this.$parent.query = '';
      }
    },
    components: {
      BarChart,
      LineChart,
      GridIcon,
      CodeIcon,
      TrendingUpIcon,
      PlayIcon,
      AlertCircleIcon,
      AlertTriangleIcon
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .key {
    color: black;
  }

  .clickable {
    cursor: pointer;
  }

  .querybar {
    position: sticky;
    top: 70px;
    z-index: 100;
    background-color: white;
    box-shadow: 0 0px 10px 5px white;
  }

  .gray {
    color: #ddd;
  }

  @media (max-width: 768px) {
    .querybar {
      position: sticky;
      top: 102px;
    }
  }
</style>
